// libs
import { LocationShapeFragmentFragment } from '@lopo-tech/lopo-api'
import { AnyAction, Dispatch } from 'redux'
import { ThunkAction } from 'redux-thunk'

// interfaces / constants
import { IAction } from 'src/actions/index'
import { IProps as ISnackBarProps } from 'src/components/snack_bar/snack_bar'
import {
  CTA_TYPE_COMPLETE_PROFILE,
  CTA_TYPE_CONFIRM_EMAIL,
  CTA_TYPE_NEW_USER_INFO,
  CTA_TYPE_VERIFY
} from 'src/constants/cta'
import {
  MODAL_TYPE_LOGIN,
  MODAL_TYPE_NONE,
  MODAL_TYPE_REGISTRATION,
  ModalType,
  PopupMenu,
  SET_OUTDATED_CLIENT_API_REVISION
} from 'src/interfaces/app-state'
import {
  IBoundingBox,
  ILocationShape
} from 'src/interfaces/location'
import { IMarker } from 'src/interfaces/marker'
import { IRootState } from 'src/reducers/interface'

// helpers
import { StoredLocationHelper } from 'src/utils/location_storage/location_storage'

export const APP_STATE_SET_POSITION = 'APP_STATE_SET_POSITION'
export const APP_STATE_SET_BOUNDING_BOX = 'APP_STATE_SET_BOUNDING_BOX'
export const APP_STATE_SET_LOCATION_SHAPE = 'APP_STATE_SET_LOCATION_SHAPE'
export const APP_STATE_TOGGLE_TOPBAR_POPUP = 'APP_STATE_TOGGLE_TOPBAR_POPUP'
export const APP_STATE_SET_MARKERS = 'APP_STATE_SET_MARKERS'
export const APP_STATE_MODAL_SHOW = 'APP_STATE_MODAL_SHOW'
export const APP_STATE_CLOSE_TOPBAR_POPUP = 'APP_STATE_CLOSE_TOPBAR_POPUP'
export const APP_STATE_OPEN_TOPBAR_POPUP = 'APP_STATE_OPEN_TOPBAR_POPUP'
export const APP_STATE_SET_NAV_SOURCE = 'APP_STATE_SET_NAV_SOURCE'
export const APP_STATE_UPDATE_CHAT_USERS_COUNT =
  'APP_STATE_UPDATE_CHAT_USERS_COUNT'
export const APP_STATE_UPDATE_CHAT_MESSAGES_COUNT =
  'APP_STATE_UPDATE_CHAT_MESSAGES_COUNT'
export const APP_STATE_SHOW_SNACK_BAR = 'APP_STATE_SHOW_SNACK_BAR'
export const APP_STATE_HIDE_SNACK_BAR = 'APP_STATE_HIDE_SNACK_BAR'

export interface ISetBoundingBoxAction {
  payload?: IBoundingBox;
  type: typeof APP_STATE_SET_BOUNDING_BOX;
}

export interface ISetLocationShapeAction {
  payload: ILocationShape;
  type: typeof APP_STATE_SET_LOCATION_SHAPE;
}

export interface ITopbarPopupMenuAction {
  payload: PopupMenu;
  type:
    | typeof APP_STATE_TOGGLE_TOPBAR_POPUP
    | typeof APP_STATE_OPEN_TOPBAR_POPUP;
}

export interface IUpdateChatUsersCount {
  payload: number | undefined;
  type: typeof APP_STATE_UPDATE_CHAT_USERS_COUNT;
}

export interface IUpdateChatMessagesCount {
  payload: number | undefined;
  type: typeof APP_STATE_UPDATE_CHAT_MESSAGES_COUNT;
}

export interface ICloseTopbarPopupMenuAction {
  type: typeof APP_STATE_CLOSE_TOPBAR_POPUP;
}

export interface ISetMarkersActions {
  payload: IMarker[];
  type: typeof APP_STATE_SET_MARKERS;
}

export interface IShowModalAction {
  payload: ModalType;
  type: typeof APP_STATE_MODAL_SHOW;
}

export interface IHideModalAction {
  payload: typeof MODAL_TYPE_NONE;
  type: typeof APP_STATE_MODAL_SHOW;
}

export interface ICheckOutdatedRevisionAction {
  type: typeof SET_OUTDATED_CLIENT_API_REVISION;
}

export interface ISetNavSourceAction {
  payload: string;
  type: typeof APP_STATE_SET_NAV_SOURCE;
}

export interface IShowSnackBar {
  payload: ISnackBarProps;
  type: typeof APP_STATE_SHOW_SNACK_BAR;
}

export interface IHideSnackBar {
  type: typeof APP_STATE_HIDE_SNACK_BAR;
}

const setLocationShape = (
  locationShape: ILocationShape
): ISetLocationShapeAction => ({
  payload: locationShape,
  type: APP_STATE_SET_LOCATION_SHAPE
})

export const toggleTopbarPopupMenu = (
  popupMenu: PopupMenu
): ITopbarPopupMenuAction => ({
  payload: popupMenu,
  type: APP_STATE_TOGGLE_TOPBAR_POPUP
})

export const closeTopbarPopupMenu = (): ICloseTopbarPopupMenuAction => ({
  type: APP_STATE_CLOSE_TOPBAR_POPUP
})

export const setMarkers = (markers: IMarker[]): ISetMarkersActions => ({
  payload: markers,
  type: APP_STATE_SET_MARKERS
})

export const showModal = (modalType: ModalType): IShowModalAction => ({
  payload: modalType,
  type: APP_STATE_MODAL_SHOW
})

export const showLoginModal = () => showModal(MODAL_TYPE_LOGIN)
export const showRegistrationModal = () => showModal(MODAL_TYPE_REGISTRATION)
export const showCompleteProfileCTA = () =>
  showModal(CTA_TYPE_COMPLETE_PROFILE)
export const showConfirmEmailCTA = () => showModal(CTA_TYPE_CONFIRM_EMAIL)
export const showVerifyCTA = () => showModal(CTA_TYPE_VERIFY)
export const showNewUserInfoCTA = () => showModal(CTA_TYPE_NEW_USER_INFO)

export const hideModal = (): IHideModalAction => ({
  payload: MODAL_TYPE_NONE,
  type: APP_STATE_MODAL_SHOW
})

export const checkOutdatedRevision = (): ICheckOutdatedRevisionAction => ({
  type: SET_OUTDATED_CLIENT_API_REVISION
})

const cleanKeysFromGQLResponse = (
  locationShape: LocationShapeFragmentFragment
): ILocationShape => {
  const cleanedLocationShape = structuredClone(locationShape)
  delete cleanedLocationShape.__typename
  delete cleanedLocationShape.boundingBox.__typename
  delete cleanedLocationShape.boundingBox.northEast.__typename
  delete cleanedLocationShape.boundingBox.southWest.__typename

  return cleanedLocationShape
}

export const changeAppLocation = (
  locationShape: ILocationShape | LocationShapeFragmentFragment,
  boundingBox?: IBoundingBox
): ThunkAction<void, IRootState, object, IAction> => {
  return (dispatch) => {
    const cleanedLocationShape = cleanKeysFromGQLResponse(locationShape)

    if (!boundingBox) {
      boundingBox = cleanedLocationShape.boundingBox
    }

    dispatch(setLocationShape(cleanedLocationShape))

    StoredLocationHelper.storeLastLocation(cleanedLocationShape)
  }
}

// this actions should not be used anywhere outside of checkToRecalculateBoundingBox()
// react-portal/src/components/map/map.tsx
export const setBoundingBox = (
  boundingBox?: IBoundingBox
): ISetBoundingBoxAction => ({
  payload: boundingBox,
  type: APP_STATE_SET_BOUNDING_BOX
})

export const setNavSource = (navSource: string): ISetNavSourceAction => ({
  payload: navSource,
  type: APP_STATE_SET_NAV_SOURCE
})

export const showSnackBar = (param: ISnackBarProps) => ({
  payload: param,
  type: APP_STATE_SHOW_SNACK_BAR
})

export const hideSnackBar = () => ({
  type: APP_STATE_HIDE_SNACK_BAR
})

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  IRootState,
  unknown,
  AnyAction
>;

export type AppDispatch = Dispatch<AnyAction> & ((action: AppThunk) => void);
